import { useState } from 'react';
import { Loader2, Lock, Phone, ShieldCheck } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';

/**
 * The sign-in step a patient hits when they tap the link in a results text.
 *
 * Rendered INLINE, in place of the results, rather than redirecting to the
 * storefront landing page the way DtpProtectedRoute does. A redirect would
 * throw away which result they were trying to open and drop them in a shop,
 * which is a strange answer to "your lab results are ready".
 *
 * This is the gate the whole link design leans on. The token in the URL names a
 * result and nothing more; the values do not load until the person holding the
 * phone proves they can receive a code at the number the clinic has on file.
 */
export function DtpResultsSignIn({
  physicianId,
  clinicName,
  phoneLast4,
  onAuthenticated
}: {
  physicianId: string;
  clinicName?: string;
  phoneLast4?: string;
  onAuthenticated: () => void;
}) {
  const { setAuth } = useDtpAuthStore();
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const requestCode = async () => {
    if (!phone.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await dtpApi.requestOtp(phone.trim(), physicianId);
      if (res.success) {
        setStep('code');
      } else {
        setError(res.error || 'We could not send a code to that number.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'We could not send a code to that number.');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await dtpApi.verifyOtp(phone.trim(), code.trim(), physicianId);
      if (res.success && res.data) {
        if (res.data.isNewPatient) {
          // The code was valid but this number is not on file as a patient of
          // this clinic, so there is nothing here to show. Deliberately NOT
          // routed into signup: creating a patient record is how someone ends
          // up staring at an empty results page wondering where their bloodwork
          // went. Send them to the clinic instead.
          setError(
            'That number verified, but we have no results on file for it at this clinic. ' +
            'If you received this link by mistake, or your results are under a different ' +
            'number, please contact the clinic.'
          );
          setStep('phone');
          setCode('');
          return;
        }
        setAuth(res.data.token, res.data.patient ?? ({} as any), physicianId);
        onAuthenticated();
      } else {
        setError(res.error || 'That code was not valid.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'That code was not valid.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto mt-10 max-w-md px-4">
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
            <Lock className="h-6 w-6 text-blue-600" />
          </div>
          <h1 className="mt-4 text-lg font-semibold text-gray-900">
            Your lab results are private
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {clinicName
              ? `Confirm your phone number to see the results ${clinicName} ordered for you.`
              : 'Confirm your phone number to see your results.'}
          </p>
        </div>

        {step === 'phone' && (
          <div className="mt-6 space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Mobile number
              {phoneLast4 && (
                <span className="ml-1 font-normal text-gray-500">
                  (ending {phoneLast4})
                </span>
              )}
            </label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && requestCode()}
                placeholder="(555) 555-5555"
                autoFocus
                className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={requestCode}
              disabled={loading || !phone.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Send me a code
            </button>
          </div>
        )}

        {step === 'code' && (
          <div className="mt-6 space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Enter the 6-digit code we texted you
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && verifyCode()}
              placeholder="000000"
              autoFocus
              className="w-full rounded-lg border border-gray-300 py-2.5 text-center text-lg tracking-[0.4em] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={verifyCode}
              disabled={loading || code.length < 6}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              View my results
            </button>
            <button
              onClick={() => {
                setStep('phone');
                setCode('');
                setError('');
              }}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              Use a different number
            </button>
          </div>
        )}

        {error && (
          <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>
        )}

        <p className="mt-6 flex items-start gap-2 text-xs text-gray-500">
          <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
          We ask for this every time so that anyone who happens to see the link in
          your messages cannot open your results.
        </p>
      </div>
    </div>
  );
}
